// ---------------------------------------------------------------------------
// EditDatasetDialog — Modal form for editing a dataset's draft metadata.
//
// Exposes only the fields the TEDH update contract allows to change after
// creation: name, description, source, usage restriction, and source
// properties. Immutable fields (data type, access level, sensitivity label,
// sub type, retention) are intentionally not editable here.
// ---------------------------------------------------------------------------

import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  Select,
  Spinner,
  Textarea,
} from '@fluentui/react-components';
import { useCallback, useEffect, useState } from 'react';

import { DATASET_USAGE_RESTRICTION_OPTIONS } from '../../../constants/index.js';
import type { DatasetUsageRestriction } from '../../../services/dataHubService.interface.js';

import type { EditDatasetDialogProps } from './EditDatasetDialog.interface.js';
import { useStyles } from './EditDatasetDialog.style.js';

const NAME_REQUIRED_ERROR = 'Dataset name is required.';
const SOURCE_PROPERTIES_INVALID_JSON_ERROR =
  'Source properties must be a valid JSON object.';
const DEFAULT_SOURCE_PROPERTIES = '{}';

function initialSourceProperties(
  props?: Record<string, unknown> | null,
): string {
  if (!props || Object.keys(props).length === 0) {
    return DEFAULT_SOURCE_PROPERTIES;
  }
  return JSON.stringify(props, null, 2);
}

export function EditDatasetDialog({
  open,
  dataset,
  onClose,
  onSubmit,
  onSaved,
}: EditDatasetDialogProps) {
  const styles = useStyles();

  const [name, setName] = useState(dataset.name);
  const [nameTouched, setNameTouched] = useState(false);
  const [description, setDescription] = useState(dataset.description ?? '');
  const [source, setSource] = useState(dataset.source ?? '');
  const [usageRestriction, setUsageRestriction] = useState<
    DatasetUsageRestriction | ''
  >(
    (dataset.usageRestriction?.[0] as DatasetUsageRestriction | undefined) ??
      '',
  );
  const [sourceProperties, setSourceProperties] = useState(
    initialSourceProperties(dataset.sourceProperties),
  );
  const [sourcePropertiesError, setSourcePropertiesError] = useState<
    string | null
  >(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Re-seed the form whenever it (re)opens or the target dataset changes.
  useEffect(() => {
    if (!open) return;
    setName(dataset.name);
    setNameTouched(false);
    setDescription(dataset.description ?? '');
    setSource(dataset.source ?? '');
    setUsageRestriction(
      (dataset.usageRestriction?.[0] as DatasetUsageRestriction | undefined) ??
        '',
    );
    setSourceProperties(initialSourceProperties(dataset.sourceProperties));
    setSourcePropertiesError(null);
    setSubmitError(null);
  }, [open, dataset]);

  const nameError = name.trim() === '' ? NAME_REQUIRED_ERROR : null;

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(async () => {
    if (nameError) {
      setNameTouched(true);
      return;
    }

    let parsedSourceProperties: Record<string, unknown> | null = null;
    const trimmedProps = sourceProperties.trim();
    if (trimmedProps !== '' && trimmedProps !== DEFAULT_SOURCE_PROPERTIES) {
      try {
        const parsed: unknown = JSON.parse(trimmedProps);
        if (
          typeof parsed !== 'object' ||
          parsed === null ||
          Array.isArray(parsed)
        ) {
          setSourcePropertiesError(SOURCE_PROPERTIES_INVALID_JSON_ERROR);
          return;
        }
        parsedSourceProperties = parsed as Record<string, unknown>;
      } catch {
        setSourcePropertiesError(SOURCE_PROPERTIES_INVALID_JSON_ERROR);
        return;
      }
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const updated = await onSubmit({
        name: name.trim(),
        description: description.trim(),
        source: source.trim(),
        usageRestriction: usageRestriction || undefined,
        sourceProperties: parsedSourceProperties,
      });
      onSaved?.(updated);
      onClose();
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : 'Failed to update dataset.',
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [
    nameError,
    name,
    description,
    source,
    usageRestriction,
    sourceProperties,
    onSubmit,
    onSaved,
    onClose,
  ]);

  return (
    <Dialog
      open={open}
      onOpenChange={(_e, data) => {
        if (!data.open) handleClose();
      }}
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Edit Dataset</DialogTitle>

          <DialogContent>
            <div className={styles.form}>
              <Field
                label="Dataset Name"
                required
                validationState={nameTouched && nameError ? 'error' : 'none'}
                validationMessage={
                  nameTouched && nameError ? nameError : undefined
                }
              >
                <Input
                  className={styles.input}
                  value={name}
                  onChange={(_e, data) => setName(data.value)}
                  onBlur={() => setNameTouched(true)}
                  disabled={isSubmitting}
                />
              </Field>

              <Field label="Description">
                <Textarea
                  className={styles.textarea}
                  value={description}
                  onChange={(_e, data) => setDescription(data.value)}
                  disabled={isSubmitting}
                />
              </Field>

              <Field label="Source">
                <Input
                  className={styles.input}
                  placeholder="e.g. teams, sharepoint"
                  value={source}
                  onChange={(_e, data) => setSource(data.value)}
                  disabled={isSubmitting}
                />
              </Field>

              <Field label="Usage Restriction">
                <Select
                  aria-label="Usage Restriction"
                  className={styles.dropdown}
                  value={usageRestriction}
                  onChange={(_e, data) =>
                    setUsageRestriction(
                      data.value as DatasetUsageRestriction | '',
                    )
                  }
                  disabled={isSubmitting}
                >
                  {DATASET_USAGE_RESTRICTION_OPTIONS.map((opt) => (
                    <option key={opt.label} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Select>
              </Field>

              <Field
                label="Source Properties"
                validationState={sourcePropertiesError ? 'error' : 'none'}
                validationMessage={sourcePropertiesError ?? undefined}
              >
                <Textarea
                  className={styles.textarea}
                  value={sourceProperties}
                  onChange={(_e, data) => {
                    setSourceProperties(data.value);
                    setSourcePropertiesError(null);
                  }}
                  disabled={isSubmitting}
                />
              </Field>
              <span className={styles.helperText}>JSON object</span>

              {submitError !== null && (
                <MessageBar intent="error" className={styles.errorBar}>
                  <MessageBarBody>{submitError}</MessageBarBody>
                </MessageBar>
              )}
            </div>
          </DialogContent>

          <DialogActions>
            <Button
              appearance="secondary"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              appearance="primary"
              icon={isSubmitting ? <Spinner size="tiny" /> : undefined}
              disabled={isSubmitting}
              onClick={() => {
                void handleSubmit();
              }}
            >
              Save
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
